import type { SxProps, Theme } from '@mui/material';
import type { LargePreviewKeyboardContext } from './useLargePreviewMediaKeyboard';

/** Shared neon palette (matches txt-editor-sync-mark in NoteForm). */
export const NOTE_NEON = {
  cyan: 'rgba(34, 211, 238, 1)',
  indigo: 'rgba(129, 140, 248, 1)',
  pink: 'rgba(232, 121, 249, 1)',
  gradient:
    'linear-gradient(125deg, rgba(34, 211, 238, 0.58) 0%, rgba(129, 140, 248, 0.6) 42%, rgba(232, 121, 249, 0.58) 100%)',
  gradientSoft:
    'linear-gradient(125deg, rgba(34, 211, 238, 0.14) 0%, rgba(129, 140, 248, 0.16) 50%, rgba(232, 121, 249, 0.14) 100%)',
} as const;

export type LargePreviewClueTheme = {
  title: string;
  detail: string;
  accent: string;
  border: string;
};

export type LargePreviewContextTheme = LargePreviewClueTheme & {
  paperBg: string;
  contentBg: string;
  actionsBg: string;
  glow: string;
};

export const LARGE_PREVIEW_GRID_CLUE: LargePreviewClueTheme = {
  title: 'Media grid',
  detail: '← → select · Space play/pause · O player',
  accent: '#0891b2',
  border: '#22d3ee',
};

export function isLargePreviewGalleryMode(mode: string): boolean {
  return mode === 'media' || mode === 'grid';
}

/** Modal chrome colors (off → gallery fallback). */
export function largePreviewThemeContext(
  context: LargePreviewKeyboardContext,
): Exclude<LargePreviewKeyboardContext, 'off'> {
  if (context === 'off') {
    return 'gallery';
  }
  return context;
}

export const LARGE_PREVIEW_CONTEXT_THEME: Record<
  Exclude<LargePreviewKeyboardContext, 'off'>,
  LargePreviewContextTheme
> = {
  gallery: {
    title: 'Preview gallery',
    detail: '← → change video · Space play/pause · G grid',
    accent: '#06b6d4',
    border: '#22d3ee',
    glow: '0 0 20px rgba(34, 211, 238, 0.28)',
    // Opaque pastels (no rgba overlays — those showed the page behind the modal).
    paperBg: 'linear-gradient(165deg, #e0f7ff 0%, #ffffff 32%, #eef0ff 72%, #fceeff 100%)',
    contentBg: '#f4fcff',
    actionsBg: '#e8f9fe',
  },
  youtube: {
    title: 'YouTube player',
    detail: 'Keys control this video only (not gallery navigation)',
    accent: '#9333ea',
    border: '#a855f7',
    glow: '0 0 20px rgba(168, 85, 247, 0.26)',
    paperBg: 'linear-gradient(165deg, #f3e8ff 0%, #ffffff 34%, #eef0ff 100%)',
    contentBg: '#faf5ff',
    actionsBg: '#f3ebff',
  },
  away: {
    title: 'App in background',
    detail: 'Note shortcuts inactive — click the app window first',
    accent: '#757575',
    border: '#bdbdbd',
    glow: 'none',
    paperBg: 'linear-gradient(180deg, #ececec 0%, #fafafa 28%, #ffffff 100%)',
    contentBg: '#f5f5f5',
    actionsBg: '#eeeeee',
  },
};

const MODAL_BASE_SX: SxProps<Theme> = {
  width: '90vw',
  maxWidth: '90vw',
  transition: 'background .25s ease, box-shadow .25s ease, opacity .25s ease',
  overflow: 'hidden',
};

export function largePreviewModalPaperSx(
  context: LargePreviewKeyboardContext,
  themed: boolean,
): SxProps<Theme> {
  if (!themed) {
    return MODAL_BASE_SX;
  }

  const theme = LARGE_PREVIEW_CONTEXT_THEME[largePreviewThemeContext(context)];
  return {
    ...MODAL_BASE_SX,
    backgroundColor: '#ffffff',
    backgroundImage: theme.paperBg,
    boxShadow: `0 0 0 3px ${theme.border}, 0 14px 52px rgba(0, 0, 0, 0.2)`,
  };
}

export function largePreviewDialogTitleSx(
  context: LargePreviewKeyboardContext,
  themed: boolean,
): SxProps<Theme> {
  if (!themed) {
    return {};
  }
  const theme = LARGE_PREVIEW_CONTEXT_THEME[largePreviewThemeContext(context)];
  return {
    borderBottom: '2px solid',
    borderColor: theme.border,
    background: theme.actionsBg,
    color: 'text.primary',
    pb: 1,
  };
}

export function largePreviewDialogContentSx(
  context: LargePreviewKeyboardContext,
  themed: boolean,
): SxProps<Theme> {
  if (!themed) {
    return {};
  }
  const theme = LARGE_PREVIEW_CONTEXT_THEME[largePreviewThemeContext(context)];
  return {
    background: theme.contentBg,
    borderTop: '1px solid',
    borderBottom: '1px solid',
    borderColor: theme.border,
  };
}

export function largePreviewDialogActionsSx(
  context: LargePreviewKeyboardContext,
  themed: boolean,
): SxProps<Theme> {
  if (!themed) {
    return {};
  }
  const theme = LARGE_PREVIEW_CONTEXT_THEME[largePreviewThemeContext(context)];
  const visual = largePreviewThemeContext(context);
  return {
    background: theme.actionsBg,
    borderTop: '2px solid',
    borderColor: theme.border,
    ...(visual !== 'away' ? { boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.35)' } : {}),
  };
}

/** Symmetric left/right gutters for media player (prev | video | next/remove). */
export const LARGE_PREVIEW_MEDIA_GUTTER_COLUMNS = {
  xs: '80px 1fr 80px',
  sm: '112px 1fr 112px',
} as const;

const LARGE_PREVIEW_CHROME_TRANSITION =
  'background .25s ease, background-color .25s ease, background-image .25s ease, border-color .25s ease, box-shadow .25s ease, color .25s ease';

type LargePreviewChromePalette = {
  border: string;
  gutterLeftBg: string;
  gutterLeftGradient: string;
  gutterRightBg: string;
  gutterRightGradient: string;
  navBg: string;
  navColor: string;
  navBorder: string;
  navShadow: string;
  navHoverBg: string;
  navDisabledBg: string;
  navDisabledColor: string;
  navDisabledBorder: string;
  toolbarShellBg: string;
  toolbarShellBorder: string;
  toolbarNavBg: string;
  toolbarNavColor: string;
  toolbarNavBorder: string;
  toolbarNavShadow: string;
  toolbarNavHoverBg: string;
  toolbarNavHoverBorder: string;
  toolbarNavHoverShadow: string;
  toolbarNavDisabledBg: string;
  toolbarNavDisabledColor: string;
  toolbarNavDisabledBorder: string;
  counterBg: string;
  counterBorder: string;
};

function largePreviewChromePalette(context: LargePreviewKeyboardContext): LargePreviewChromePalette {
  const visual = largePreviewThemeContext(context);
  const theme = LARGE_PREVIEW_CONTEXT_THEME[visual];

  if (visual === 'youtube') {
    return {
      border: theme.border,
      gutterLeftBg: 'rgba(168, 85, 247, 0.10)',
      gutterLeftGradient:
        'linear-gradient(180deg, rgba(168, 85, 247, 0.22) 0%, rgba(129, 140, 248, 0.14) 55%, rgba(232, 121, 249, 0.10) 100%)',
      gutterRightBg: 'rgba(168, 85, 247, 0.10)',
      gutterRightGradient:
        'linear-gradient(180deg, rgba(232, 121, 249, 0.10) 0%, rgba(129, 140, 248, 0.14) 45%, rgba(168, 85, 247, 0.22) 100%)',
      navBg: 'rgba(147, 51, 234, 0.24)',
      navColor: '#581c87',
      navBorder: 'rgba(168, 85, 247, 0.55)',
      navShadow: '0 10px 24px rgba(168, 85, 247, 0.18)',
      navHoverBg: 'rgba(147, 51, 234, 0.34)',
      navDisabledBg: 'rgba(147, 51, 234, 0.10)',
      navDisabledColor: 'rgba(88, 28, 135, 0.35)',
      navDisabledBorder: 'rgba(168, 85, 247, 0.22)',
      toolbarShellBg: theme.actionsBg,
      toolbarShellBorder: theme.border,
      toolbarNavBg: 'rgba(147, 51, 234, 0.16)',
      toolbarNavColor: '#581c87',
      toolbarNavBorder: 'rgba(168, 85, 247, 0.5)',
      toolbarNavShadow: '0 6px 16px rgba(168, 85, 247, 0.12)',
      toolbarNavHoverBg: 'rgba(147, 51, 234, 0.32)',
      toolbarNavHoverBorder: 'rgba(168, 85, 247, 0.8)',
      toolbarNavHoverShadow: '0 10px 24px rgba(168, 85, 247, 0.22)',
      toolbarNavDisabledBg: 'rgba(147, 51, 234, 0.06)',
      toolbarNavDisabledColor: 'rgba(88, 28, 135, 0.35)',
      toolbarNavDisabledBorder: 'rgba(168, 85, 247, 0.2)',
      counterBg: 'rgba(168, 85, 247, 0.10)',
      counterBorder: 'rgba(168, 85, 247, 0.35)',
    };
  }

  if (visual === 'away') {
    return {
      border: theme.border,
      gutterLeftBg: 'rgba(158, 158, 158, 0.14)',
      gutterLeftGradient: 'linear-gradient(180deg, #e8e8e8 0%, #f2f2f2 100%)',
      gutterRightBg: 'rgba(158, 158, 158, 0.14)',
      gutterRightGradient: 'linear-gradient(180deg, #f2f2f2 0%, #e8e8e8 100%)',
      navBg: 'rgba(158, 158, 158, 0.22)',
      navColor: '#616161',
      navBorder: 'rgba(189, 189, 189, 0.85)',
      navShadow: 'none',
      navHoverBg: 'rgba(158, 158, 158, 0.30)',
      navDisabledBg: 'rgba(158, 158, 158, 0.10)',
      navDisabledColor: 'rgba(97, 97, 97, 0.35)',
      navDisabledBorder: 'rgba(189, 189, 189, 0.35)',
      toolbarShellBg: theme.actionsBg,
      toolbarShellBorder: theme.border,
      toolbarNavBg: 'rgba(158, 158, 158, 0.16)',
      toolbarNavColor: '#616161',
      toolbarNavBorder: 'rgba(189, 189, 189, 0.75)',
      toolbarNavShadow: 'none',
      toolbarNavHoverBg: 'rgba(158, 158, 158, 0.28)',
      toolbarNavHoverBorder: 'rgba(158, 158, 158, 0.95)',
      toolbarNavHoverShadow: 'none',
      toolbarNavDisabledBg: 'rgba(158, 158, 158, 0.08)',
      toolbarNavDisabledColor: 'rgba(97, 97, 97, 0.35)',
      toolbarNavDisabledBorder: 'rgba(189, 189, 189, 0.35)',
      counterBg: 'rgba(158, 158, 158, 0.12)',
      counterBorder: 'rgba(189, 189, 189, 0.55)',
    };
  }

  return {
    border: theme.border,
    gutterLeftBg: 'rgba(34, 211, 238, 0.10)',
    gutterLeftGradient:
      'linear-gradient(180deg, rgba(34, 211, 238, 0.20) 0%, rgba(129, 140, 248, 0.14) 55%, rgba(232, 121, 249, 0.10) 100%)',
    gutterRightBg: 'rgba(34, 211, 238, 0.10)',
    gutterRightGradient:
      'linear-gradient(180deg, rgba(232, 121, 249, 0.10) 0%, rgba(129, 140, 248, 0.14) 45%, rgba(34, 211, 238, 0.20) 100%)',
    navBg: 'rgba(6, 182, 212, 0.24)',
    navColor: '#075985',
    navBorder: 'rgba(34, 211, 238, 0.55)',
    navShadow: '0 10px 24px rgba(34, 211, 238, 0.18)',
    navHoverBg: 'rgba(6, 182, 212, 0.34)',
    navDisabledBg: 'rgba(6, 182, 212, 0.10)',
    navDisabledColor: 'rgba(7, 89, 133, 0.35)',
    navDisabledBorder: 'rgba(34, 211, 238, 0.22)',
    toolbarShellBg: theme.actionsBg,
    toolbarShellBorder: theme.border,
    toolbarNavBg: 'rgba(6, 182, 212, 0.16)',
    toolbarNavColor: '#075985',
    toolbarNavBorder: 'rgba(34, 211, 238, 0.5)',
    toolbarNavShadow: '0 6px 16px rgba(34, 211, 238, 0.12)',
    toolbarNavHoverBg: 'rgba(6, 182, 212, 0.32)',
    toolbarNavHoverBorder: 'rgba(34, 211, 238, 0.8)',
    toolbarNavHoverShadow: '0 10px 24px rgba(34, 211, 238, 0.22)',
    toolbarNavDisabledBg: 'rgba(6, 182, 212, 0.06)',
    toolbarNavDisabledColor: 'rgba(7, 89, 133, 0.35)',
    toolbarNavDisabledBorder: 'rgba(34, 211, 238, 0.2)',
    counterBg: 'rgba(34, 211, 238, 0.10)',
    counterBorder: 'rgba(34, 211, 238, 0.35)',
  };
}

export const largePreviewSideGutterBaseSx = {
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  p: 1,
  boxSizing: 'border-box',
  transition: LARGE_PREVIEW_CHROME_TRANSITION,
} as const;

export function largePreviewSideNavBtnSx(
  context: LargePreviewKeyboardContext,
  themed: boolean,
): SxProps<Theme> {
  if (!themed) {
    return {
      width: '100%',
      flex: 1,
      minHeight: 72,
      borderRadius: 1.5,
    };
  }

  const palette = largePreviewChromePalette(context);
  return {
    width: '100%',
    flex: 1,
    minHeight: 72,
    borderRadius: 1.5,
    bgcolor: palette.navBg,
    color: palette.navColor,
    border: `1px solid ${palette.navBorder}`,
    boxShadow: palette.navShadow,
    transition: LARGE_PREVIEW_CHROME_TRANSITION,
    '&:hover': { bgcolor: palette.navHoverBg },
    '&.Mui-disabled': {
      bgcolor: palette.navDisabledBg,
      color: palette.navDisabledColor,
      borderColor: palette.navDisabledBorder,
      boxShadow: 'none',
    },
  };
}

export function largePreviewSideGutterLeftSx(
  context: LargePreviewKeyboardContext,
  themed: boolean,
): SxProps<Theme> {
  if (!themed) {
    return {
      ...largePreviewSideGutterBaseSx,
      justifyContent: 'center',
      borderRight: '1px solid',
      borderColor: 'divider',
    };
  }

  const palette = largePreviewChromePalette(context);
  return {
    ...largePreviewSideGutterBaseSx,
    bgcolor: palette.gutterLeftBg,
    backgroundImage: palette.gutterLeftGradient,
    borderRight: '1px solid',
    borderColor: palette.border,
    justifyContent: 'center',
  };
}

export function largePreviewSideGutterRightSx(
  context: LargePreviewKeyboardContext,
  themed: boolean,
): SxProps<Theme> {
  if (!themed) {
    return {
      ...largePreviewSideGutterBaseSx,
      alignItems: 'center',
      gap: 1,
      borderLeft: '1px solid',
      borderColor: 'divider',
    };
  }

  const palette = largePreviewChromePalette(context);
  return {
    ...largePreviewSideGutterBaseSx,
    bgcolor: palette.gutterRightBg,
    backgroundImage: palette.gutterRightGradient,
    borderLeft: '1px solid',
    borderColor: palette.border,
    alignItems: 'center',
    gap: 1,
  };
}

export function largePreviewMediaPlayerShellSx(
  context: LargePreviewKeyboardContext,
  themed: boolean,
): SxProps<Theme> {
  const base = {
    height: '80vh',
    display: 'grid',
    gridTemplateColumns: LARGE_PREVIEW_MEDIA_GUTTER_COLUMNS,
    borderRadius: 1,
    overflow: 'hidden',
    border: '1px solid',
    transition: LARGE_PREVIEW_CHROME_TRANSITION,
  } as const;

  if (!themed) {
    return {
      ...base,
      bgcolor: 'background.paper',
      borderColor: 'divider',
    };
  }

  const palette = largePreviewChromePalette(context);
  const theme = LARGE_PREVIEW_CONTEXT_THEME[largePreviewThemeContext(context)];
  return {
    ...base,
    bgcolor: theme.contentBg,
    borderColor: palette.border,
    boxShadow: theme.glow === 'none' ? 'none' : `inset 0 0 0 1px ${palette.border}`,
  };
}

export function largePreviewToolbarNavBtnSx(
  context: LargePreviewKeyboardContext,
  themed: boolean,
): SxProps<Theme> {
  const toolbarChipSx = {
    px: 1.25,
    py: 0.35,
    borderRadius: 1.25,
    boxSizing: 'border-box',
  } as const;

  const toolbarIconBtnBase = {
    ...toolbarChipSx,
    width: '100%',
    minWidth: 72,
    height: 'auto',
    minHeight: 'unset',
    maxHeight: 'none',
    padding: '0.35rem 1.25rem',
  } as const;

  if (!themed) {
    return {
      ...largePreviewButtonFocusSx,
      ...toolbarIconBtnBase,
    };
  }

  const palette = largePreviewChromePalette(context);
  return {
    ...largePreviewButtonFocusSx,
    ...toolbarIconBtnBase,
    bgcolor: palette.toolbarNavBg,
    color: palette.toolbarNavColor,
    border: `1px solid ${palette.toolbarNavBorder}`,
    boxShadow: palette.toolbarNavShadow,
    transition: LARGE_PREVIEW_CHROME_TRANSITION,
    '& .nav-icon-single': { display: 'flex' },
    '& .nav-icon-double': { display: 'none' },
    '&:hover:not(.Mui-disabled)': {
      bgcolor: palette.toolbarNavHoverBg,
      borderColor: palette.toolbarNavHoverBorder,
      boxShadow: palette.toolbarNavHoverShadow,
      transform: 'scale(1.03)',
      '& .nav-icon-single': { display: 'none' },
      '& .nav-icon-double': { display: 'flex' },
    },
    '&.Mui-disabled': {
      bgcolor: palette.toolbarNavDisabledBg,
      color: palette.toolbarNavDisabledColor,
      borderColor: palette.toolbarNavDisabledBorder,
      boxShadow: 'none',
    },
  };
}

export function largePreviewToolbarShellSx(
  context: LargePreviewKeyboardContext,
  themed: boolean,
): SxProps<Theme> {
  if (!themed) {
    return {
      borderColor: 'divider',
      bgcolor: 'background.paper',
    };
  }

  const palette = largePreviewChromePalette(context);
  return {
    borderColor: palette.toolbarShellBorder,
    bgcolor: palette.toolbarShellBg,
    transition: LARGE_PREVIEW_CHROME_TRANSITION,
  };
}

export function largePreviewToolbarCounterSx(
  context: LargePreviewKeyboardContext,
  themed: boolean,
): SxProps<Theme> {
  const base = {
    px: 1.25,
    py: 0.35,
    borderRadius: 1.25,
    boxSizing: 'border-box',
    fontSize: '1rem',
    fontWeight: 700,
    fontVariantNumeric: 'tabular-nums',
    color: 'text.primary',
    lineHeight: 1.1,
    minWidth: 56,
    width: '100%',
    maxWidth: 120,
    textAlign: 'center',
    display: 'inline-block',
    transition: LARGE_PREVIEW_CHROME_TRANSITION,
  } as const;

  if (!themed) {
    return base;
  }

  const palette = largePreviewChromePalette(context);
  return {
    ...base,
    bgcolor: palette.counterBg,
    border: `1px solid ${palette.counterBorder}`,
  };
}

/** Total height of Large Preview footer (toolbar + Close). */
export const LARGE_PREVIEW_FOOTER_HEIGHT_PX = 50;

/** Inner white toolbar strip inside the footer. */
export const LARGE_PREVIEW_TOOLBAR_HEIGHT_PX = 40;

/** Footer: media toolbar stretches full width; Close stays on the right. */
export function largePreviewDialogActionsBarSx(
  context: LargePreviewKeyboardContext,
  themed: boolean,
): SxProps<Theme> {
  const themeStyles = largePreviewDialogActionsSx(context, themed);
  return {
    ...(typeof themeStyles === 'object' && themeStyles !== null && !Array.isArray(themeStyles)
      ? themeStyles
      : {}),
    width: '100%',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 1.5,
    px: 1.5,
    py: 0.25,
    minHeight: LARGE_PREVIEW_FOOTER_HEIGHT_PX,
    maxHeight: LARGE_PREVIEW_FOOTER_HEIGHT_PX,
    '& > :not(style) ~ :not(style)': { ml: 0 },
  };
}

/** Grid: pointer hover overrides toolbar/keyboard selection highlight. */
export const largePreviewGridRootSx: SxProps<Theme> = {
  display: 'grid',
  gridTemplateColumns: {
    xs: '1fr',
    sm: 'repeat(2, 1fr)',
    md: 'repeat(3, 1fr)',
    lg: 'repeat(4, 1fr)',
  },
  gap: 1.5,
  alignItems: 'stretch',
  '&:hover > *': {
    opacity: 0.42,
    filter: 'grayscale(55%) brightness(0.88)',
  },
  '&:hover > *:hover': {
    opacity: 1,
    filter: 'none',
    transform: 'scale(1.07)',
    boxShadow: 8,
    zIndex: 2,
    borderColor: 'primary.main',
  },
  '&:has([data-grid-selected="true"]):not(:hover) > [data-grid-selected="false"]': {
    opacity: 0.42,
    filter: 'grayscale(55%) brightness(0.88)',
  },
  '&:has([data-grid-selected="true"]):not(:hover) > [data-grid-selected="true"]': {
    opacity: 1,
    filter: 'none',
    transform: 'scale(1.07)',
    boxShadow: 8,
    zIndex: 2,
    borderColor: 'primary.main',
  },
};

const GRID_TILE_TRANSITION =
  'transform .18s ease-out, box-shadow .18s ease-out, border-color .12s, opacity .18s ease-out, filter .18s ease-out';

/** Single grid tile — pair with `data-grid-selected` on the element. */
export function largePreviewGridTileSx(selected: boolean): SxProps<Theme> {
  return {
    bgcolor: 'black',
    borderRadius: 1,
    overflow: 'hidden',
    border: '1px solid',
    borderColor: selected ? 'primary.main' : 'divider',
    boxShadow: selected ? 8 : 1,
    position: 'relative',
    transition: GRID_TILE_TRANSITION,
    cursor: 'pointer',
  };
}

/** Neon/gradient focus ring for Large Preview dialog buttons (matches txt-editor-sync-mark). */
export const largePreviewButtonFocusSx = {
  borderRadius: '7px',
  transition: 'background .18s, box-shadow .18s, border-color .18s, outline .18s',
  '&.Mui-focusVisible, &:focus-visible': {
    borderColor: 'rgba(34, 211, 238, 0.65)',
    background: NOTE_NEON.gradient,
    boxShadow:
      '0 0 12px rgba(34, 211, 238, 0.72), 0 0 26px rgba(129, 140, 248, 0.55), 0 0 44px rgba(232, 121, 249, 0.42), inset 0 1px 0 rgba(255, 255, 255, 0.32)',
    outline: '1.5px solid rgba(34, 211, 238, 0.65)',
    outlineOffset: '2px',
  },
} satisfies SxProps<Theme>;
